/**
 * A volunteer's departments live in a single `volunteers.department` text column,
 * comma-separated when they belong to more than one. Splitting it is a security-
 * relevant rule -- it decides which department chats a volunteer can read -- and the
 * SQL side does it too, so the two must agree exactly.
 *
 * The canonical form is the one in the task policies
 * (20250301000003_tasks_multi_assignment.sql): split on ',', TRIM, compare LOWER.
 * Anything that changes here has to change there as well, or chat membership and task
 * visibility will disagree about who is in "Logistics ".
 */

/** Every department named in a raw `volunteers.department` value, trimmed, blanks dropped. */
export function splitDepartments(raw: string | null | undefined): string[] {
  if (!raw) return [];
  return raw
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);
}

/**
 * Is this volunteer in this department? Case-insensitive and whitespace-insensitive on
 * both sides, matching `LOWER(TRIM(...))` in the SQL predicates.
 */
export function isInDepartment(raw: string | null | undefined, department: string): boolean {
  const wanted = department.trim().toLowerCase();
  if (!wanted) return false;
  return splitDepartments(raw).some((d) => d.toLowerCase() === wanted);
}

/** One department and how many volunteers are in it. */
export type DepartmentCount = { label: string; value: number };

/**
 * Count volunteers per department across raw `volunteers.department` values.
 *
 * Case-insensitive, because every other reader of this column is: the task policies, the
 * department-chat membership predicate, and the departments board all treat 'Logistics'
 * and 'logistics' as one department, so a chart that split them into two slices would be
 * describing a grouping the app does not actually have. The label kept is the first
 * spelling seen, so the organization's own capitalization is what gets displayed.
 */
export function countDepartments(raws: (string | null | undefined)[]): DepartmentCount[] {
  const byKey = new Map<string, DepartmentCount>();
  for (const raw of raws) {
    for (const dept of splitDepartments(raw)) {
      const key = dept.toLowerCase();
      const existing = byKey.get(key);
      if (existing) existing.value += 1;
      else byKey.set(key, { label: dept, value: 1 });
    }
  }
  return [...byKey.values()];
}

/**
 * Distinct department names from a list of raw values, in first-seen order.
 * Case-insensitive de-duplication keeps the first spelling encountered, so the label
 * shown stays the one the organization actually typed.
 */
export function uniqueDepartments(raws: (string | null | undefined)[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of raws) {
    for (const d of splitDepartments(raw)) {
      const key = d.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(d);
    }
  }
  return out;
}

/** Items that share a department; `label` is null for the ones in no department. */
export interface DepartmentGroup<T> {
  label: string | null;
  items: T[];
}

/**
 * Group items by department, for showing a roster split into departments.
 *
 * Same rules as the rest of this file: `rawDepartmentsOf` returns raw
 * `volunteers.department` values, which are split and compared case-insensitively, and a
 * group keeps the first spelling seen. Someone in two departments appears in both -- the
 * departments board does the same, and on a shift it tells the organizer they can cover
 * either. Groups are alphabetical; the no-department group, if any, comes last.
 */
export function groupByDepartment<T>(
  items: T[],
  rawDepartmentsOf: (item: T) => (string | null | undefined)[],
): DepartmentGroup<T>[] {
  const byKey = new Map<string, DepartmentGroup<T>>();
  const none: T[] = [];
  for (const item of items) {
    const departments = rawDepartmentsOf(item).flatMap((raw) => splitDepartments(raw));
    if (departments.length === 0) {
      none.push(item);
      continue;
    }
    const placed = new Set<string>();
    for (const dept of departments) {
      const key = dept.toLowerCase();
      if (placed.has(key)) continue;
      placed.add(key);
      const group = byKey.get(key);
      if (group) group.items.push(item);
      else byKey.set(key, { label: dept, items: [item] });
    }
  }
  const groups = [...byKey.values()].sort((a, b) => (a.label as string).localeCompare(b.label as string));
  if (none.length > 0) groups.push({ label: null, items: none });
  return groups;
}
